import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment.local';
import { RestService } from './rest.service';
import { LoginResponse } from '../models/dto/login-response';
import {
  MFABackupCodesResponse,
  MFASetupResponse,
  MFAStatusResponse,
} from '../models/dto/mfa-response';
import { ResponseDto } from '../models/dto/response';

/**
 * MfaService is the single home for all MFA (TOTP) REST calls. It also holds the
 * short-lived enrollment/verification tokens IN MEMORY only — they are never
 * persisted, so a hard page reload on an MFA page intentionally loses them and
 * the component sends the user back to /login.
 */
@Injectable({
  providedIn: 'root',
})
export class MfaService {
  /** Enroll token from a `mfa_setup_required` login (authorizes setup + confirm). */
  setupToken: string = '';
  /** Pre-auth token from a `mfa_required` login (used by verify, in the body). */
  preAuthToken: string = '';

  constructor(private _restService: RestService) {}

  /** Builds the auth header carrying the one-off setup token (login enrollment). */
  private setupHeader(setupToken?: string): { [name: string]: string } | undefined {
    return setupToken ? { [environment.authHeader]: setupToken } : undefined;
  }

  /**
   * Start enrollment: returns the otpauth URL (for the QR code) and the secret.
   * When `setupToken` is given (login enrollment, no session) it is sent as the
   * auth header; otherwise the AuthInterceptor attaches the session JWT (profile).
   */
  mfaSetup(setupToken?: string) {
    return this._restService.post<MFASetupResponse>(
      '/auth/mfa/setup',
      {},
      this.setupHeader(setupToken)
    );
  }

  /** Confirm enrollment with the first TOTP code; returns one-time backup codes. */
  mfaConfirm(code: string, setupToken?: string) {
    return this._restService.post<MFABackupCodesResponse>(
      '/auth/mfa/confirm',
      { code },
      this.setupHeader(setupToken)
    );
  }

  /** Second login step: exchange pre-auth token + code for a full session. */
  mfaVerify(code: string) {
    return this._restService.post<LoginResponse>('/auth/mfa/verify', {
      pre_auth_token: this.preAuthToken,
      code,
    });
  }

  /** Disable MFA for the current (non-admin) user after verifying a TOTP code. */
  mfaDisable(code: string) {
    return this._restService.delete<ResponseDto>('/auth/mfa/disable', { code });
  }

  /** Regenerate backup codes after verifying a current TOTP code. */
  regenerateBackupCodes(code: string) {
    return this._restService.post<MFABackupCodesResponse>(
      '/auth/mfa/backup-codes/regenerate',
      { code }
    );
  }

  /** Admin: reset another user's MFA configuration (e.g. lost device). */
  adminResetMfa(guid: string) {
    return this._restService.post<ResponseDto>(`/admin/mfa/reset/${guid}`, {});
  }

  /** Current user's MFA status (enabled + whether they may disable it). */
  mfaStatus() {
    return this._restService.get<MFAStatusResponse>('/auth/mfa/status');
  }
}
